import React, { useEffect, useCallback, useMemo, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { injectIntl, WrappedComponentProps } from "react-intl";
import { useAppDispatch, useAppSelector } from "../../types/hooks";
import {
  useDrawerContext,
  useDrawerUpdate,
  useDrawerWidth,
  useDrawerWidthUpdate,
} from "../../ThemeContext";
import { getMenuItemsForRole, type LayoutRole, type UserPermissions } from "../../libs";
import { selectEffectivePermissionPayload } from "../../store/permissionSelectors";
import { selectUserRole } from "../../store/authSelectors";
import { selectLoggedInUsersDetailsRecord } from "../../store/loginDetailsSelectors";
import { selectMyProfileUserDetails } from "../../store/myProfileSlice";
import { userActions } from "../../actions";
import { useProfilePictureUrl } from "../../hooks/useProfilePictureUrl";
import Header from "../Header";
import LayoutSystemBanners from "./LayoutSystemBanners";
import LayoutTermsModal from "./LayoutTermsModal";
import { useLayoutSystemBanners } from "./hooks/useLayoutSystemBanners";
import { useLayoutTermsGate } from "./hooks/useLayoutTermsGate";
import { APP_MODAL_PORTAL_ID } from "../../constants/alert.constants";
import { useViewportHostSizeVars } from "../../hooks/useViewportHostSizeVars";
import { clearViewClientsAndCreateNewCase } from "../../store/casesClientSearchSlice";
import { clearCasesClientScope } from "../../store/casesSlice";
import { navigateToCasesList } from "../../utils/cases/navigateToClientCases";
import Sidebar from "../Sidebar";
import "./Layout.scss";

type LoggedInUserDetailsShape = {
  business?: { business_is_active?: boolean; business_name?: string; business_shortcode?: string };
  business_link?: { business_name?: string; client_id?: string | number };
  client_id?: string | number;
  email?: string;
  username?: string;
};

interface LayoutProps extends WrappedComponentProps<"intl"> {
  users: any;
  children?: React.ReactNode;
}

function Layout({ intl, users, children }: LayoutProps) {
  useViewportHostSizeVars();
  const dispatch = useAppDispatch();
  const drawerOpen = useDrawerContext();
  const toggleDrawerContext = useDrawerUpdate();
  const drawerWidth = useDrawerWidth();
  const history = useHistory();
  const { pathname } = useLocation();
  const setDrawerWidthContext = useDrawerWidthUpdate();
  const prevPathnameRef = useRef(pathname);

  useEffect(() => {
    const previousPath = prevPathnameRef.current;
    prevPathnameRef.current = pathname;
    if (previousPath === "/cases" && pathname !== "/cases") {
      dispatch(clearCasesClientScope());
      dispatch(clearViewClientsAndCreateNewCase());
    }
  }, [pathname, dispatch]);

  const [role_of_user, setRoleOfUser] = React.useState<string | undefined>();

  const roleOfUserFromRedux = useAppSelector(selectUserRole);
  const userInfoFromReduxLogDetails = useAppSelector(
    (state) => selectLoggedInUsersDetailsRecord(state) as LoggedInUserDetailsShape | undefined
  );
  const myProfilePicturePath = useAppSelector(
    (state) => selectMyProfileUserDetails(state)?.profile_picture ?? null
  );

  const userPermissions = useAppSelector(selectEffectivePermissionPayload) as UserPermissions;

  const currentLayout = useMemo((): LayoutRole => {
    const role = roleOfUserFromRedux || role_of_user;
    if (role === "sysadmin") return "sysadmin";
    if (role === "business-user") {
      return userInfoFromReduxLogDetails?.business?.business_is_active
        ? "business-active"
        : "business-inactive";
    }
    if (role === "client") return "client";
    if (role === "trp" || role === "trpdoc") return "trp";
    return null;
  }, [roleOfUserFromRedux, role_of_user, userInfoFromReduxLogDetails]);

  const menuItems = useMemo(
    () => getMenuItemsForRole(currentLayout, intl.formatMessage.bind(intl), userPermissions),
    [currentLayout, intl, userPermissions]
  );

  const companyName = useMemo(() => {
    switch (currentLayout) {
      case "sysadmin":
      case "trp":
        return intl.formatMessage({ id: "taxManagementHeader" });
      case "business-active":
      case "business-inactive":
        return userInfoFromReduxLogDetails?.business?.business_name ?? "";
      case "client":
        return userInfoFromReduxLogDetails?.business_link?.business_name ?? "";
      default:
        return "";
    }
  }, [currentLayout, userInfoFromReduxLogDetails, intl]);

  const details = users?.items?.data?.user_details;
  const rawProfilePicture = myProfilePicturePath ?? details?.profile_picture ?? undefined;
  const profilePicturePath =
    typeof rawProfilePicture === "string"
      ? rawProfilePicture
      : ((rawProfilePicture as { url?: string } | null | undefined)?.url ?? rawProfilePicture);
  const profilePictureUrl = useProfilePictureUrl(profilePicturePath);

  const {
    banners: systemBanners,
    isLoading: isSystemBannersLoading,
    hasSession: hasSystemBannerSession,
  } = useLayoutSystemBanners();

  const {
    isOpen: isTermsModalOpen,
    pendingTerms,
    isSubmitting: isTermsSubmitting,
    actionError: termsActionError,
    onAccept: onTermsAccept,
    onReject: onTermsReject,
  } = useLayoutTermsGate();

  const helpDropdownContent = useMemo(
    () => (
      <LayoutSystemBanners
        banners={systemBanners}
        isLoading={isSystemBannersLoading}
        hasSession={hasSystemBannerSession}
      />
    ),
    [systemBanners, isSystemBannersLoading, hasSystemBannerSession]
  );

  const headerProps = useMemo(() => {
    const firstName = details?.first_name;
    const surname = details?.surname ?? "";
    const fullName = [details?.first_name, details?.middle_name, details?.surname]
      .filter(Boolean)
      .join(" ");
    const isClientLayout = currentLayout === "client";
    return {
      companyName,
      avatarSrc: profilePictureUrl ?? undefined,
      avatarAlt: firstName ? `${firstName} ${surname}`.trim() : "User avatar",
      avatarInitials: details?.initials,
      userName: fullName || undefined,
      onProfileClick: () => history.push("/profile"),
      onLogout: () => dispatch(userActions.logout()),
      helpDropdownContent,
      clientNavbarDropdown: isClientLayout,
      linkedBusinessName: isClientLayout
        ? userInfoFromReduxLogDetails?.business_link?.business_name
        : undefined,
      linkedBusinessBadge: isClientLayout ? "Default" : undefined,
    };
  }, [
    companyName,
    currentLayout,
    details,
    profilePictureUrl,
    history,
    dispatch,
    userInfoFromReduxLogDetails?.business_link?.business_name,
    helpDropdownContent,
  ]);

  const syncRoleFromRedux = useCallback(() => {
    setRoleOfUser(roleOfUserFromRedux);
  }, [roleOfUserFromRedux]);

  useEffect(() => {
    syncRoleFromRedux();
  }, [syncRoleFromRedux]);

  const bodyStyle = useMemo<React.CSSProperties>(
    () => ({ marginLeft: drawerWidth }),
    [drawerWidth]
  );

  if (!currentLayout) {
    return (
      <div className="viewport-scale-host">
        <div className="layout__root" aria-hidden />
        <div id={APP_MODAL_PORTAL_ID} className="app-modal-portal" />
      </div>
    );
  }

  return (
    <div className="viewport-scale-host">
      <div
        className={`layout__root${isTermsModalOpen ? " layout__root--terms-gated" : ""}`}
        data-layout-role={currentLayout}
        aria-hidden={isTermsModalOpen ? true : undefined}
      >
        <header className="layout__header" role="banner">
          <Header key={pathname} {...headerProps} />
        </header>
        <LayoutTermsModal
          open={isTermsModalOpen}
          terms={pendingTerms}
          isSubmitting={isTermsSubmitting}
          actionError={termsActionError}
          onAccept={onTermsAccept}
          onReject={onTermsReject}
        />
        <div className="layout__main">
          <Sidebar
            menuItems={menuItems}
            open={drawerOpen}
            activePathname={pathname}
            onNavigate={(id) => {
              if (id === "/cases") {
                navigateToCasesList(dispatch, history);
                return;
              }
              history.push(id);
            }}
            onExpandChange={(expanded) => {
              if (expanded !== drawerOpen) toggleDrawerContext();
            }}
            onDrawerWidthChange={setDrawerWidthContext}
          />
          <main className="layout__body" style={bodyStyle} role="main">
            {children}
          </main>
        </div>
      </div>
      <div id={APP_MODAL_PORTAL_ID} className="app-modal-portal" />
    </div>
  );
}

export default injectIntl(Layout);
